import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    Check,
    Loader2,
    Upload,
    Copy,
    QrCode,
    ShieldCheck,
    XCircle,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { api, API } from "../../lib/api";
import axios from "axios";

const PACKAGE_AMOUNT = {
    disha: "969",
    niti: "1,969",
    samvad: "3,969",
};

export default function QuestionnaireShell({
    title,
    tagline,
    packageKey,
    sections,
}) {
    // Flow: 0 = Payment, 1 = Contact, 2..n-1 = sections, n = Review+Submit (implicit via last section)
    const [step, setStep] = useState(0);
    const [contact, setContact] = useState({ name: "", email: "", phone: "" });
    const [answers, setAnswers] = useState({});
    const [screenshot, setScreenshot] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [payment, setPayment] = useState(null); // {id, status, reason, confidence}
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [submissionId, setSubmissionId] = useState("");
    const [upi, setUpi] = useState({
        upi_id: "ridikashah349-3@okhdfcbank",
        upi_name: "Karishma Shah",
    });

    useEffect(() => {
        api.get("/config")
            .then((r) => setUpi(r.data))
            .catch(() => {});
    }, []);

    const paymentVerified = payment?.status === "verified";

    const steps = [
        {
            key: "payment",
            title: "Secure Payment",
            description: "Pay via UPI, upload your receipt, and we'll verify it in seconds.",
            isPayment: true,
        },
        {
            key: "contact",
            title: "Your Details",
            description: "We'll use this to send you your report.",
            isContact: true,
        },
        ...sections,
    ];
    const total = steps.length;
    const progress = ((step + 1) / total) * 100;
    const current = steps[step];
    const amount = PACKAGE_AMOUNT[packageKey];

    const setAnswer = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));
    const setField = (k, v) => setContact((c) => ({ ...c, [k]: v }));

    const copyUpi = () => {
        navigator.clipboard.writeText(upi.upi_id);
        toast.success("UPI ID copied");
    };

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 8 * 1024 * 1024) {
            toast.error("Max 8MB.");
            return;
        }
        setScreenshot(f);
        setPayment(null); // reset verification on new file
    };

    const verify = async () => {
        if (!contact.name || !contact.email || !contact.phone) {
            toast.error("Please fill name, email and phone first (next step).");
            return;
        }
        if (!screenshot) {
            toast.error("Please upload your payment screenshot.");
            return;
        }
        setVerifying(true);
        try {
            const fd = new FormData();
            fd.append("package", packageKey);
            fd.append("name", contact.name);
            fd.append("email", contact.email);
            fd.append("phone", contact.phone);
            fd.append("screenshot", screenshot);
            const { data } = await axios.post(
                `${API}/payments/verify`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setPayment(data);
            if (data.status === "verified") {
                toast.success("Payment verified — you can continue.");
            } else {
                toast.error(data.reason || "Payment could not be verified.");
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Verification failed. Please try again.",
            );
        } finally {
            setVerifying(false);
        }
    };

    const canAdvance = () => {
        if (step === 0) return paymentVerified;
        if (step === 1)
            return contact.name && contact.email && contact.phone;
        return true;
    };

    const next = () => {
        if (!canAdvance()) {
            if (step === 0) {
                if (!payment) {
                    toast.error("Please upload your payment screenshot and verify.");
                } else {
                    toast.error("Payment is not yet verified.");
                }
                return;
            }
            if (step === 1) {
                toast.error("Please fill your name, email and phone.");
                return;
            }
        }
        if (step < total - 1) setStep(step + 1);
    };
    const prev = () => step > 0 && setStep(step - 1);

    const submit = async () => {
        if (!paymentVerified) {
            toast.error("Payment required.");
            setStep(0);
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("name", contact.name);
            fd.append("email", contact.email);
            fd.append("phone", contact.phone);
            fd.append("answers", JSON.stringify(answers));
            fd.append("payment_id", payment.id);
            const { data } = await axios.post(
                `${API}/submissions/${packageKey}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setSubmissionId(data.id);
            setDone(true);
            toast.success("Submission received.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not submit. Try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-[#FDFAF5] flex items-center justify-center px-6">
                <div
                    data-testid="questionnaire-success"
                    className="max-w-xl w-full text-center bg-white border border-[#E6DFD3] rounded-2xl p-10 md:p-12"
                >
                    <div className="w-14 h-14 rounded-full bg-[#1A5C53]/10 text-[#1A5C53] flex items-center justify-center mx-auto">
                        <Check size={24} />
                    </div>
                    <h1 className="font-serif text-3xl md:text-4xl mt-6 text-[#0D1B1A]">
                        Thank you, {contact.name.split(" ")[0]}.
                    </h1>
                    <p className="mt-4 text-[#4A5A58]">
                        Your {title} submission is safely recorded. We'll
                        reach out within 48 hours with your next steps.
                    </p>
                    <div className="mt-6 bg-[#F3EFE9]/70 border border-[#E6DFD3] rounded-xl p-5 text-left">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#758684]">
                            Your tracking reference
                        </div>
                        <div className="font-mono text-sm text-[#0D1B1A] mt-2 break-all">
                            {submissionId}
                        </div>
                        <p className="text-xs text-[#4A5A58] mt-3">
                            Save this ID. Use it on the{" "}
                            <Link to="/track" className="text-[#1A5C53] underline">
                                Track page
                            </Link>{" "}
                            with your email to see live progress.
                        </p>
                    </div>
                    <div className="mt-8 flex gap-3 justify-center">
                        <Link
                            to="/track"
                            className="border border-[#1A5C53] text-[#1A5C53] hover:bg-[#1A5C53]/5 rounded-full px-6 py-3 font-medium"
                        >
                            Track Submission
                        </Link>
                        <Link
                            to="/"
                            className="bg-[#1A5C53] text-white hover:bg-[#13423B] rounded-full px-6 py-3 font-medium"
                        >
                            Home
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFAF5]">
            <div className="bg-[#0D1B1A] text-white">
                <div className="max-w-4xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
                    <Link to="/" className="font-serif text-2xl font-medium">
                        Dhan<span className="text-[#D4944C]">Nniti</span>
                    </Link>
                    <div className="text-xs tracking-[0.2em] uppercase text-[#9FB1AE]">
                        {title}
                    </div>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
                <div className="mb-10">
                    <div className="flex items-center justify-between text-xs text-[#758684] mb-3">
                        <span>
                            Step {step + 1} of {total}
                        </span>
                        <span>{Math.round(progress)}% complete</span>
                    </div>
                    <Progress value={progress} className="h-1.5 bg-[#E6DFD3]" />
                </div>

                <div className="mb-10">
                    <div className="text-xs tracking-[0.2em] uppercase text-[#1A5C53] mb-2">
                        {tagline}
                    </div>
                    <h1 className="font-serif text-3xl md:text-4xl text-[#0D1B1A] leading-tight">
                        {current.title}
                    </h1>
                    {current.description && (
                        <p className="mt-3 text-[#4A5A58]">
                            {current.description}
                        </p>
                    )}
                </div>

                {/* === PAYMENT STEP === */}
                {current.isPayment && (
                    <div className="space-y-8">
                        <div className="bg-white border border-[#E6DFD3] rounded-2xl p-6 md:p-8">
                            <div className="grid md:grid-cols-2 gap-8 items-start">
                                <div>
                                    <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#D4944C]">
                                        <QrCode size={14} /> UPI Payment
                                    </div>
                                    <div className="mt-4 text-sm text-[#4A5A58]">
                                        Amount
                                    </div>
                                    <div className="font-serif text-5xl text-[#0D1B1A] mt-1">
                                        ₹{amount}
                                    </div>
                                    <div className="mt-6 text-sm text-[#4A5A58]">
                                        Pay to
                                    </div>
                                    <div className="font-medium text-[#0D1B1A] mt-1">
                                        {upi.upi_name}
                                    </div>
                                    <div className="mt-2 flex items-center gap-2 bg-[#F3EFE9] border border-[#E6DFD3] rounded-lg px-3 py-2">
                                        <span
                                            data-testid="upi-id-display"
                                            className="font-mono text-sm text-[#0D1B1A] break-all"
                                        >
                                            {upi.upi_id}
                                        </span>
                                        <button
                                            onClick={copyUpi}
                                            data-testid="copy-upi-btn"
                                            className="ml-auto text-[#1A5C53] hover:text-[#13423B] flex-shrink-0"
                                            aria-label="Copy UPI ID"
                                        >
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                    <p className="mt-4 text-xs text-[#758684]">
                                        Open any UPI app (GPay, PhonePe,
                                        Paytm, bank app) → Scan the QR or
                                        paste the UPI ID → Pay ₹{amount}.
                                    </p>
                                </div>
                                <div className="flex flex-col items-center">
                                    <div className="bg-white p-3 rounded-xl border border-[#E6DFD3]">
                                        <img
                                            src="/upi-qr.png"
                                            alt="UPI QR"
                                            className="w-56 h-56 object-contain"
                                        />
                                    </div>
                                    <p className="text-xs text-[#758684] mt-3 text-center">
                                        Scan with any UPI app
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Payer contact (needed before verify) */}
                        <div className="bg-white border border-[#E6DFD3] rounded-2xl p-6 md:p-8">
                            <div className="text-xs tracking-[0.2em] uppercase text-[#1A5C53] mb-4">
                                Your details
                            </div>
                            <div className="grid sm:grid-cols-3 gap-4">
                                <Input
                                    data-testid="pay-name"
                                    value={contact.name}
                                    onChange={(e) =>
                                        setField("name", e.target.value)
                                    }
                                    placeholder="Full name"
                                    className="bg-[#FDFAF5] border-[#E6DFD3]"
                                />
                                <Input
                                    type="email"
                                    data-testid="pay-email"
                                    value={contact.email}
                                    onChange={(e) =>
                                        setField("email", e.target.value)
                                    }
                                    placeholder="you@example.com"
                                    className="bg-[#FDFAF5] border-[#E6DFD3]"
                                />
                                <Input
                                    data-testid="pay-phone"
                                    value={contact.phone}
                                    onChange={(e) =>
                                        setField("phone", e.target.value)
                                    }
                                    placeholder="+91 98xxx xxxxx"
                                    className="bg-[#FDFAF5] border-[#E6DFD3]"
                                />
                            </div>
                        </div>

                        {/* Upload + verify */}
                        <div className="bg-white border border-[#E6DFD3] rounded-2xl p-6 md:p-8">
                            <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#1A5C53]">
                                <Sparkles size={14} /> AI-verified receipt
                            </div>
                            <p className="text-xs text-[#758684] mt-2">
                                After paying, upload the success screenshot
                                (PNG, JPG, or WEBP — max 8MB). Our AI will
                                verify it in a few seconds.
                            </p>
                            <label
                                htmlFor="screenshot"
                                className={`mt-5 block border border-dashed rounded-xl p-6 cursor-pointer text-center transition-colors ${
                                    screenshot
                                        ? "border-[#1A5C53] bg-[#F3EFE9]/40"
                                        : "border-[#E6DFD3] hover:border-[#1A5C53] bg-[#FDFAF5]"
                                }`}
                            >
                                <Upload
                                    size={20}
                                    className="mx-auto text-[#758684]"
                                />
                                <div className="mt-2 text-sm text-[#4A5A58]">
                                    {screenshot
                                        ? screenshot.name
                                        : "Click to choose screenshot"}
                                </div>
                                <input
                                    id="screenshot"
                                    data-testid="payment-screenshot-input"
                                    type="file"
                                    accept="image/*"
                                    onChange={onFileChange}
                                    className="hidden"
                                />
                            </label>

                            <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
                                <button
                                    onClick={verify}
                                    disabled={
                                        verifying ||
                                        !screenshot ||
                                        paymentVerified
                                    }
                                    data-testid="verify-payment-btn"
                                    className="inline-flex items-center gap-2 bg-[#1A5C53] text-white hover:bg-[#13423B] disabled:opacity-50 rounded-full px-6 py-3 font-medium"
                                >
                                    {verifying ? (
                                        <Loader2
                                            size={16}
                                            className="animate-spin"
                                        />
                                    ) : paymentVerified ? (
                                        <ShieldCheck size={16} />
                                    ) : (
                                        <Sparkles size={16} />
                                    )}
                                    {paymentVerified
                                        ? "Verified"
                                        : verifying
                                          ? "Verifying…"
                                          : "Verify payment"}
                                </button>

                                {payment && paymentVerified && (
                                    <div className="flex items-center gap-2 text-sm text-[#1A5C53]">
                                        <ShieldCheck size={16} />
                                        AI verified ({payment.confidence}%
                                        confidence)
                                    </div>
                                )}
                                {payment && !paymentVerified && (
                                    <div className="flex items-start gap-2 text-sm text-[#9B2226] max-w-md">
                                        <XCircle
                                            size={16}
                                            className="mt-0.5 flex-shrink-0"
                                        />
                                        <span>
                                            {payment.reason ||
                                                "Could not verify this screenshot."}{" "}
                                            Please re-upload a clearer success
                                            screenshot.
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* === CONTACT STEP === */}
                {current.isContact && (
                    <div className="space-y-5">
                        <div>
                            <Label>Full Name</Label>
                            <Input
                                data-testid="q-contact-name"
                                value={contact.name}
                                onChange={(e) =>
                                    setField("name", e.target.value)
                                }
                                placeholder="Rohit Sharma"
                                className="mt-1.5 bg-white border-[#E6DFD3]"
                            />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    data-testid="q-contact-email"
                                    value={contact.email}
                                    onChange={(e) =>
                                        setField("email", e.target.value)
                                    }
                                    placeholder="you@example.com"
                                    className="mt-1.5 bg-white border-[#E6DFD3]"
                                />
                            </div>
                            <div>
                                <Label>Phone</Label>
                                <Input
                                    data-testid="q-contact-phone"
                                    value={contact.phone}
                                    onChange={(e) =>
                                        setField("phone", e.target.value)
                                    }
                                    placeholder="+91 98xxx xxxxx"
                                    className="mt-1.5 bg-white border-[#E6DFD3]"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* === SECTION STEP === */}
                {current.questions && (
                    <div className="space-y-6">
                        {current.questions.map((q) => (
                            <div key={q.id}>
                                <Label htmlFor={q.id} className="font-medium">
                                    {q.label}
                                </Label>
                                {q.notes && (
                                    <p className="text-xs text-[#758684] mt-1">
                                        {q.notes}
                                    </p>
                                )}
                                {q.type === "textarea" ? (
                                    <Textarea
                                        id={q.id}
                                        data-testid={`q-${q.id}`}
                                        value={answers[q.id] || ""}
                                        onChange={(e) =>
                                            setAnswer(q.id, e.target.value)
                                        }
                                        placeholder={q.placeholder || ""}
                                        className="mt-2 bg-white border-[#E6DFD3] min-h-[90px]"
                                    />
                                ) : (
                                    <Input
                                        id={q.id}
                                        type={q.type || "text"}
                                        data-testid={`q-${q.id}`}
                                        value={answers[q.id] || ""}
                                        onChange={(e) =>
                                            setAnswer(q.id, e.target.value)
                                        }
                                        placeholder={q.placeholder || ""}
                                        className="mt-2 bg-white border-[#E6DFD3]"
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex items-center justify-between mt-12 pt-6 border-t border-[#E6DFD3]">
                    <button
                        onClick={prev}
                        disabled={step === 0}
                        data-testid="q-prev-btn"
                        className="inline-flex items-center gap-2 text-[#4A5A58] disabled:opacity-30 hover:text-[#1A5C53] px-4 py-2"
                    >
                        <ArrowLeft size={16} /> Back
                    </button>
                    {step < total - 1 ? (
                        <button
                            onClick={next}
                            data-testid="q-next-btn"
                            className="inline-flex items-center gap-2 bg-[#1A5C53] text-white hover:bg-[#13423B] rounded-full px-6 py-3 font-medium disabled:opacity-50"
                            disabled={step === 0 && !paymentVerified}
                        >
                            Continue <ArrowRight size={16} />
                        </button>
                    ) : (
                        <button
                            onClick={submit}
                            disabled={submitting}
                            data-testid="q-submit-btn"
                            className="inline-flex items-center gap-2 bg-[#D4944C] text-[#0D1B1A] hover:bg-[#E8B870] disabled:opacity-60 rounded-full px-6 py-3 font-medium"
                        >
                            {submitting && (
                                <Loader2 size={16} className="animate-spin" />
                            )}
                            Submit Assessment
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
